import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuth, useHeaderData } from '@screentone/addon-auth-wrapper';
import { Card as SkedCard } from '@screentone/addon-sked';
import { IconData } from '@screentone/addon-sked/dist/components/Card/types';
import {
  Box,
  Button,
  Dropdown,
  Group,
  IconCameraOff,
  IconCheckCircle,
  IconClipboard,
  IconClock,
  IconDisabled,
  IconThreeDotsVer,
  Typography,
  Wrapper
} from '@screentone/core';
import classNames from 'classnames';

// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { RemoveEntity } from 'components/context-menu-actions/remove-entity/RemoveEntity';
import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
import { useAllessehContentUpdate } from 'contexts/allesseh-content-update/useAllessehContentUpdate';
import { useAltSummOverrideContext } from 'contexts/altsumm-override/AltSummOverrideContext';
import { DuplicateItemType } from 'contexts/content-id-tracking/ContentIdTrackingContext';
import { useContentIdTracking } from 'contexts/content-id-tracking/useContentIdTracking';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelStableContext } from 'contexts/datamodel/useDataModel';
import { HIGHLIGHT_GROUPS } from 'contexts/highlight/HighlightContext';
import { useHighlight } from 'contexts/highlight/useHighlight';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { useSummarianContext } from 'contexts/summarian/useSummarianContext';
import { ArticleItem, CollectionDto, MobileArticleItem } from 'data/generated/graphql';
import { OKTA_ENV } from 'features/auth/components/OktaRouter';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { commandFSearchStyles, HiddenSearchableTextSpan } from 'hooks/useCommandFSearch';
import { useContentConstants } from 'hooks/useContentConstants';
import { defaultImage, defaultSummaryHeadline } from 'utils/article';
import { getContentEnv } from 'utils/cms';
import { formatInTimeZone } from 'utils/dates';
import env from 'utils/env';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import useIsBlockedAgency from 'utils/hooks/useIsBlockedAgency';
import { isItemAllowedInZone } from 'utils/mobile-content';
import { safelyParseContent } from 'utils/temp';
import { decodeHtmlEntities } from 'utils/text';
import { resizeIMImage } from 'utils/url';
import styles from './Article.module.scss';
import { brandIcon } from './brandIcon';
import CardBullets, { ALLOWED_BRANDS_FOR_BULLETS } from './Bullets';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { CancelledEmbargoArticle } from './CancelledEmbargoArticle';
import { getTypeAndBrandIcons } from './TypeAndBrandToken';
import { CommonContentProps } from '../commonContentProps';
import { CONTENT_CARD_TEST_IDS } from '../skedCardTestIds';

import { getUrlToShow, getUrlToShowType } from 'components/altsumm-modal/AltSummModal.utils';
import type { ArticlePromotionLogData } from 'contexts/datamodel/DataModelContext.core';
import { useAltSummFields } from 'contexts/summarian/summarian.utils';

const IM_WIDTH = {
  THUMBNAIL: 40,
  ENLARGED: 180
};

const NEW_BADGE_DURATION_MS = 5 * 60 * 1000;

const buildSlugTokens = (
  allessehContent: AllessehContent,
  displayDate: string,
  currentProperty: string | null,
  tooltipInPortal: boolean = true,
  iconSvgOverrides?: Record<string, string>
) => {
  const { typeIcons, brandIcon: brandIconData } = getTypeAndBrandIcons(
    allessehContent,
    currentProperty,
    iconSvgOverrides
  );
  const contentEnv = getContentEnv(allessehContent.data.attributes.source_url);
  const isProdId = allessehContent.data.id.includes('WP-');
  const tooltipConfig = { tooltipPosition: 'top' as const, tooltipProps: tooltipInPortal ? { usePortal: true } : {} };

  const brandToken: IconData | null = brandIconData
    ? { icon: brandIconData.icon as unknown as ReactNode, tooltip: brandIconData.description, ...tooltipConfig }
    : null;

  const otherTokens: IconData[] = typeIcons.map(({ icon, description }) => ({
    icon: icon as unknown as ReactNode,
    tooltip: description,
    ...tooltipConfig
  }));

  if (allessehContent.data.attributes.content_status === 'live') {
    otherTokens.push({
      icon: IconCheckCircle as unknown as ReactNode,
      color: 'blurple',
      tooltip: `Published: ${displayDate}`,
      ...tooltipConfig
    });
  }

  if (allessehContent.data.attributes.content_status === 'embargo') {
    otherTokens.push({
      icon: IconDisabled as unknown as ReactNode,
      color: 'lava',
      tooltip: 'Embargo',
      ...tooltipConfig
    });
  }

  if (env.OKTA_ENV !== OKTA_ENV.PRD && (contentEnv === 'prod' || isProdId)) {
    otherTokens.push({
      color: 'tangerine',
      token: true,
      text: 'Prod',
      tooltip: 'Content exists in production',
      ...tooltipConfig
    });
  }

  return { brandToken, otherTokens };
};

function isLiveCoverageEvent(allessehContent: AllessehContent): boolean {
  const { product, section_type: sectionType, template } = allessehContent.data.attributes;
  return product.includes('Live Coverage') && (sectionType === 'stack' || template === 'stack');
}

function getContentSlug(allessehContent: AllessehContent): string {
  const {
    id = '',
    attributes: { associations, wsjdn_story_slugs: wsjdnStorySlugs }
  } = allessehContent.data;
  let type = allessehContent.data.attributes.type || '';

  if (type.length === 0) {
    console.warn(`Article with id ${id} has no type. Defaulting to "article"`);
    type = 'article';
  }

  const liveCoverageEvent = isLiveCoverageEvent(allessehContent);
  const isVideoOrAudio = ['audio', 'video'].includes(type.toLowerCase());

  let slug = associations?.slug ?? '';
  if (liveCoverageEvent && !slug) {
    slug = associations?.root_id ?? '';
  } else if (isVideoOrAudio) {
    slug = wsjdnStorySlugs?.[0] ?? '';
  }

  return slug;
}

type ResetErrorBoundaryType = (...args: any[]) => void;

interface ErrorArticleProps {
  allessehId: string;
  hierarchyId: string;
  resetErrorBoundary: ResetErrorBoundaryType;
  isDraft?: boolean;
  noBorder?: boolean;
  boxPadding?: 'xs' | 'sm' | 'smd' | 'md' | 'mlg' | 'lg' | 'xl';
  isCollectionItem?: boolean;
  renderActions?: () => ReactNode;
}

const ErrorArticle = ({
  allessehId,
  hierarchyId,
  resetErrorBoundary,
  isDraft,
  noBorder = false,
  boxPadding = 'sm',
  isCollectionItem = false,
  renderActions
}: ErrorArticleProps) => {
  const copyIdToClipboard = async () => {
    await navigator.clipboard.writeText(allessehId);
  };

  useLayoutEffect(
    () => () => {
      resetErrorBoundary();
    },
    [allessehId, resetErrorBoundary]
  );

  return (
    <>
      <Box
        padding={{ all: boxPadding }}
        className={classNames(styles.errorArticle, { [styles.noBorder]: noBorder })}
        data-allesseh-id={allessehId}
        data-testid="page-content-card-container"
        data-model-hierarchy-id={hierarchyId}
      >
        <Group gap="none">
          <div className={styles.imageContainer} data-testid="content-card-image-container">
            <div className={classNames(styles.image, styles.noImage)} data-testid="thumbnail" />
          </div>

          <Typography
            componentEl="div"
            variant="bodytext"
            margin={{ left: 'sm' }}
            className={styles.headline}
            data-testid="content-card-headline-label"
          >
            This article has an error and cannot be rendered properly. (ID: {allessehId})
            <Button tertiary margin={{ right: 'md' }} icon={IconClipboard} onClick={copyIdToClipboard}>
              Copy ID
            </Button>
          </Typography>

          {renderActions ? (
            <Dropdown
              padding={{ all: 'none' }}
              position="right"
              trigger={<IconThreeDotsVer color="asphalt" />}
              data-testid="page-content-ellipsis-icon"
            >
              {renderActions()}
            </Dropdown>
          ) : (
            isDraft &&
            !isCollectionItem && (
              <Dropdown
                padding={{ all: 'none' }}
                position="right"
                trigger={<IconThreeDotsVer color="asphalt" />}
                data-testid="page-content-ellipsis-icon"
              >
                <Wrapper onClick={resetErrorBoundary} padding={{ all: 'smd' }}>
                  <RemoveEntity hierarchyId={hierarchyId} text="Remove from module" />
                </Wrapper>
              </Dropdown>
            )
          )}
        </Group>
      </Box>
    </>
  );
};

export interface BareArticleProps extends CommonEntityProps, CommonContentProps {
  data: ArticleItem;
  renderActions?: () => React.ReactNode;
  parentQueryHierarchyId?: string;
  isOverflow?: boolean;
  rulesConfig?: Record<string, unknown> | null | undefined;
  articlePromotionLogData?: ArticlePromotionLogData | null;
  tooltipInPortal?: boolean;
  isCollectionItem?: boolean;
  collectionMetadata?: CollectionDto['metadata'];
}

export interface ArticleProps extends BareArticleProps {
  noBorder?: boolean;
  boxPadding?: 'xs' | 'sm' | 'smd' | 'md' | 'mlg' | 'lg' | 'xl';
  collectionMetadata?: CollectionDto['metadata'];
}

const BareArticle = ({
  data: article,
  isDraft,
  renderActions,
  parentQueryHierarchyId,
  parentHierarchyId,
  index,
  isOverflow,
  rulesConfig,
  articlePromotionLogData,
  tooltipInPortal = true,
  isCollectionItem,
  collectionMetadata
}: BareArticleProps) => {
  const { data: contentConstants } = useContentConstants();
  const { currentProperty } = useAuth();
  const { trackContentIds, ignoreContentIds, getDuplicateClassName } = useContentIdTracking();
  const { generateHierarchyId } = useDataModelStableContext();
  const { state } = useHeaderData();
  const { selectedAltSummVariant: altSummVariant } = useSummarianContext();
  const { shouldHighlight, clearItems } = useHighlight();
  const { isSection } = usePageTypeSettingsContext();

  const hierarchyId = generateHierarchyId(article, parentHierarchyId, index);
  const ignoreHierarchyIds = parentQueryHierarchyId ? [parentQueryHierarchyId] : [];
  const clockTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [updatePromotionDelta, setUpdatePromotionDelta] = useState(false);
  const [newBadgeTick, setNewBadgeTick] = useState(false);

  const { data: publicationSettings } = usePublicationSettings();

  const queryConfigIconOverrides = useMemo(() => {
    const configs = publicationSettings?.publicationSetting.baseQueryConfigs ?? [];
    return Object.fromEntries(
      configs.filter((qc) => qc.enabled && qc.iconSvgPath).map((qc) => [qc.name, qc.iconSvgPath as string])
    );
  }, [publicationSettings]);

  const isAllowedInMultizone = useMemo(
    () => {
      // If the hierarchyId contains zone, it means this article is part of a multizone module.
      if (hierarchyId.includes('zone')) {
        return isItemAllowedInZone({
          item: article as MobileArticleItem,
          rulesConfig
        });
      }

      /**
       * If the hierarchyId does not contain zone,
       * it means this article is not part of a multizone module.
       * ie: items in the content search
       */

      return undefined;
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [article.attributes.id]
  );

  const isSearchContentResult = hierarchyId === 'article';

  // Do not track changes for articles that are part of the search results,
  // since the SearchByQueryTerm component handles tracking for those articles separately
  const parsedContent = useMemo(() => safelyParseContent<AllessehContent>(article), [article]);
  const updatedContent = useAllessehContentUpdate(article.attributes.id, {
    enabled: !isSearchContentResult,
    initialContent: parsedContent
  });
  const allessehContent = updatedContent ?? parsedContent;

  const { clearOverride } = useAltSummOverrideContext();
  useEffect(() => {
    if (updatedContent) {
      clearOverride(article.attributes.id, altSummVariant);
    }
  }, [altSummVariant, article.attributes.id, clearOverride, updatedContent]);

  const actionButtons = renderActions?.();

  const isBlockedAgency = useIsBlockedAgency();

  const altSummFields = useAltSummFields(allessehContent, altSummVariant);
  const resolvedHeadline = isSection ? defaultSummaryHeadline(allessehContent) : altSummFields.headline;
  const headline = resolvedHeadline?.trim() ? resolvedHeadline : (article.attributes.product ?? '');
  const urlToShow = isSection ? defaultImage(allessehContent) : getUrlToShow(altSummFields);

  const isBlockedImage = isBlockedAgency(altSummFields.imageCredit, collectionMetadata);

  useEffect(() => {
    if (isDraft) {
      trackContentIds(hierarchyId, article.attributes.id);

      return () => {
        ignoreContentIds(hierarchyId, article.attributes.id);
        clearItems(HIGHLIGHT_GROUPS.ITP_NEW_ITEMS);
      };
    }

    return () => {};
  }, [article.attributes.id, clearItems, hierarchyId, ignoreContentIds, isDraft, trackContentIds]);

  const formatTimestamp = useCallback(
    (timestamp: string | number | Date) =>
      formatInTimeZone(timestamp, publicationSettings?.publicationSetting.preferredTimezone, 'MM/dd/yyyy hh:mm aa', {
        showTimezoneAbbreviation: true
      }),
    [publicationSettings?.publicationSetting.preferredTimezone]
  );

  const promotionDate = useMemo(
    () => (articlePromotionLogData?.promotedAt ? formatTimestamp(new Date(articlePromotionLogData.promotedAt)) : null),
    [articlePromotionLogData, formatTimestamp]
  );

  const formattedPromotionDelta = useMemo(() => {
    if (!articlePromotionLogData?.promotedAt) {
      return '';
    }

    const promotionDelta = Date.now() - new Date(articlePromotionLogData.promotedAt).getTime();
    const minutesSincePromotion = Math.floor(promotionDelta / 1000 / 60);

    const hours = Math.floor(minutesSincePromotion / 60);
    const minutes = `${minutesSincePromotion % 60}`.padStart(2, '0');

    return `${hours}:${minutes}`;
    // Updates the formatted time every minute
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePromotionDelta, articlePromotionLogData]);

  useEffect(() => {
    if (promotionDate !== null && isDraft) {
      if (clockTimer.current) {
        clearInterval(clockTimer.current);
      }

      // Start the clock timer to update the promotion delta every minute
      clockTimer.current = setInterval(() => setUpdatePromotionDelta((prev) => !prev), 60000);
    }

    return () => {
      if (clockTimer.current) {
        clearInterval(clockTimer.current);
      }
    };
  }, [promotionDate, isDraft]);

  const newBadgeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const publishedDatetime = allessehContent.data.attributes.published_datetime_utc;
  const { isNew, msSincePublished } = useMemo(() => {
    if (!publishedDatetime) {
      return { isNew: false, msSincePublished: null };
    }
    const ms = Date.now() - new Date(publishedDatetime).getTime();
    return { isNew: ms < NEW_BADGE_DURATION_MS, msSincePublished: ms };
    // newBadgeTick forces re-evaluation when the 5-minute window expires
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedDatetime, newBadgeTick]);

  useEffect(() => {
    if (msSincePublished === null) {
      return () => {};
    }
    const msUntilExpiry = NEW_BADGE_DURATION_MS - msSincePublished;
    if (msUntilExpiry <= 0) {
      return () => {};
    }
    newBadgeTimer.current = setTimeout(() => setNewBadgeTick((prev) => !prev), msUntilExpiry);
    return () => {
      if (newBadgeTimer.current) {
        clearTimeout(newBadgeTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedDatetime]);

  const imageUrlThumbnail =
    useMemo(
      () =>
        resizeIMImage(
          urlToShow,
          {
            width: IM_WIDTH.THUMBNAIL,
            size: 1
          },
          getUrlToShowType(altSummFields)
        ),
      [urlToShow, altSummFields]
    ) ?? '';
  const imageUrlEnlarged = useMemo(() => resizeIMImage(urlToShow, { width: IM_WIDTH.ENLARGED }), [urlToShow]) ?? '';
  const displayDate = formatTimestamp(allessehContent.data.attributes.published_datetime_utc);

  const isEmbargo = allessehContent.data.attributes.content_status === 'embargo';
  let embargoDate = '';

  if (isEmbargo && allessehContent.data.attributes.embargo_datetime_utc) {
    embargoDate = formatTimestamp(allessehContent.data.attributes.embargo_datetime_utc);
  }

  const {
    major_revision_date: majorRevisionDate,
    published_datetime: publishedDatetimeLocal,
    published_datetime_utc: publishedDatetimeUtcAttr
  } = allessehContent.data.attributes;
  const isMajorRepublish = !!majorRevisionDate && majorRevisionDate !== publishedDatetimeLocal;
  // major_revision_date has no timezone info but appears to be in ET — add the ET→UTC offset (derived from published_datetime vs published_datetime_utc) to get a UTC timestamp
  const etOffsetMs = new Date(publishedDatetimeUtcAttr).getTime() - new Date(publishedDatetimeLocal).getTime();
  const updatedDate = isMajorRepublish ? formatTimestamp(new Date(majorRevisionDate).getTime() + etOffsetMs) : null;

  const { brandToken, otherTokens } = buildSlugTokens(
    allessehContent,
    displayDate,
    currentProperty,
    tooltipInPortal,
    queryConfigIconOverrides
  );
  const slug = getContentSlug(allessehContent);

  if (promotionDate && isDraft) {
    otherTokens.push({
      icon: IconClock as unknown as ReactNode,
      color: 'tangerine',
      tooltip: `Added to page: ${promotionDate}`,
      tooltipPosition: 'top',
      text: formattedPromotionDelta,
      tooltipProps: tooltipInPortal ? { usePortal: true } : {}
    });
  }

  const itpNewContentClassName = shouldHighlight(article.attributes.id, HIGHLIGHT_GROUPS.ITP_NEW_ITEMS)
    ? styles.itpNewContent
    : '';
  const itpStaleContentClassName =
    shouldHighlight(article.attributes.id, HIGHLIGHT_GROUPS.ITP_STALE_ITEMS) && isDraft ? styles.itpStaleContent : '';
  const overflowClassName = isOverflow ? styles.overflow : '';
  const duplicateClassName = isDraft
    ? getDuplicateClassName(
        state.headerConfig.currentTheme ?? 'light',
        DuplicateItemType.CARD,
        article.attributes.id,
        ignoreHierarchyIds
      )
    : '';

  const multizoneErrorHighlightClassName = classNames({
    [styles.multizoneModuleErrorHighlight]: isAllowedInMultizone === false
  });

  // isDraft === true is to enforce boolean value since it can be undefined
  const showBlockedAgencyError =
    (isDraft === true || isCollectionItem === true) && isBlockedImage && !isSearchContentResult;

  const isBlockedImageClassName = classNames({
    [styles.restrictedAgencyErrorHighlight]: showBlockedAgencyError
  });

  return (
    <div data-allesseh-id={allessehContent.data.id} data-model-hierarchy-id={hierarchyId}>
      <SkedCard
        className={classNames(
          itpNewContentClassName,
          itpStaleContentClassName,
          overflowClassName,
          duplicateClassName,
          multizoneErrorHighlightClassName,
          isBlockedImageClassName,
          // Support Command + F search functionality
          commandFSearchStyles.commandFSearchContainer
        )}
        testIds={CONTENT_CARD_TEST_IDS}
      >
        <SkedCard.Header
          menu={actionButtons}
          slug={slug}
          brandToken={brandToken ?? undefined}
          slugTokens={otherTokens}
          testIds={CONTENT_CARD_TEST_IDS.header}
        />
        <SkedCard.Content
          type="curation"
          headline={decodeHtmlEntities(headline)}
          image={imageUrlThumbnail}
          validImage={!showBlockedAgencyError}
          imageEnlarged={!showBlockedAgencyError ? imageUrlEnlarged : undefined}
          validImageTooltipProps={{
            usePortal: true
          }}
          icon={brandIcon({
            allessehContent,
            product: article.attributes.product,
            publicationMap: contentConstants?.publicationMap
          })}
          imageOverride={
            <div className={styles.noIcon}>
              <IconCameraOff size="lg" className={styles.image} />
            </div>
          }
          testIds={CONTENT_CARD_TEST_IDS.content}
        />
        <HiddenSearchableTextSpan searchableText={article.attributes.id} />
        {!!currentProperty && ALLOWED_BRANDS_FOR_BULLETS.includes(currentProperty) && isDraft && (
          <CardBullets bullets={altSummFields.bulletsStructured} />
        )}
        <SkedCard.Footer
          filing={
            isEmbargo
              ? `Scheduled: ${embargoDate}`
              : `Published: ${displayDate}${isNew ? '  New' : ''}${updatedDate ? `   Updated: ${updatedDate}` : ''}`
          }
          testIds={CONTENT_CARD_TEST_IDS.footer}
        />
      </SkedCard>
    </div>
  );
};

export const Article = (articleProps: ArticleProps) => {
  const { generateHierarchyId, getArticlePromotionLogByOriginId } = useDataModelStableContext();
  const hierarchyId = generateHierarchyId(articleProps.data, articleProps.parentHierarchyId, articleProps.index);
  const articlePromotionLogData = getArticlePromotionLogByOriginId(articleProps.data.attributes.originId);

  if (articleProps.data.attributes.isCancelledEmbargo) {
    return (
      <CancelledEmbargoArticle
        allessehId={articleProps.data.attributes.id}
        hierarchyId={hierarchyId}
        isDraft={articleProps.isDraft}
        isCollectionItem={articleProps.isCollectionItem}
      />
    );
  }

  const containsAllessehContent = articleProps.data.content?.startsWith('{');

  if (!containsAllessehContent) {
    return (
      <ErrorArticle
        allessehId={articleProps.data.attributes.id}
        hierarchyId={hierarchyId}
        boxPadding={articleProps.boxPadding}
        noBorder={articleProps.noBorder}
        resetErrorBoundary={() => {}}
        isDraft={articleProps.isDraft ?? true}
        isCollectionItem={articleProps.isCollectionItem}
      />
    );
  }

  return (
    <ErrorBoundary
      fallbackRender={({ resetErrorBoundary }) => (
        <ErrorArticle
          allessehId={articleProps.data.attributes.id}
          hierarchyId={hierarchyId}
          boxPadding={articleProps.boxPadding}
          noBorder={articleProps.noBorder}
          resetErrorBoundary={resetErrorBoundary as ResetErrorBoundaryType}
          isDraft={articleProps.isDraft}
          renderActions={articleProps.renderActions}
        />
      )}
    >
      <BareArticle {...articleProps} articlePromotionLogData={articlePromotionLogData} />
    </ErrorBoundary>
  );
};
